// Use a distinct element name: Horizon also defines <sticky-add-to-cart>.
class PremiumStickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.select = this.querySelector('[data-sticky-variant]');
    this.idInput = this.querySelector('[data-sticky-variant-id]');
    this.quantity = this.querySelector('[data-sticky-quantity-input]');
    this.addButton = this.querySelector('[data-sticky-add]');
    this.price = this.querySelector('[data-sticky-price]');
    this.compare = this.querySelector('[data-sticky-compare]');
    this.image = this.querySelector('[data-sticky-image]');
    this.label = this.querySelector('[data-sticky-label]');
    this.selectedVariant = this.querySelector('[data-sticky-selected-variant]');
    this.variants = JSON.parse(this.querySelector('[data-sticky-variants]').textContent);
    this.mainForm = [...document.querySelectorAll('product-form-component')].find((form) =>
      !this.contains(form) && form.dataset.productId === this.dataset.productId && form.closest('.ppis, .product-information')
    );
    this.mainProduct = this.mainForm?.closest('.ppis, .product-information');
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('click', this.onClick, { signal });
    document.addEventListener('change', this.syncFromMain, { signal });
    this.setVariant(this.mainForm?.querySelector('input[name="id"]')?.value || this.idInput.value);
    const mainQuantity = this.mainProduct?.querySelector('[data-quantity-input], input[name="quantity"]');
    if (mainQuantity) this.quantity.value = mainQuantity.value;
    this.normalizeQuantity();
    this.sizeObserver = new ResizeObserver(() => {
      const bottom = parseFloat(getComputedStyle(this).bottom) || 0;
      document.body.style.setProperty('--premium-sticky-cart-clearance', `${this.offsetHeight + bottom + 16}px`);
    });
    this.sizeObserver.observe(this);
    if (this.dataset.showMode === 'always' || this.dataset.designMode === 'true' || !this.mainForm) {
      this.classList.add('is-visible');
    } else {
      this.observeMainForm(signal);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.sizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
  }

  observeMainForm(signal) {
    const target = this.mainForm.querySelector('.ppis__actions') || this.mainForm;
    const update = () => {
      // A form below the viewport has not been reached yet.
      this.classList.toggle('is-visible', target.getBoundingClientRect().bottom <= 0);
    };
    // Scroll events also cover jumps from below the viewport directly to above it,
    // which can leave the IntersectionObserver's intersection state unchanged.
    const scheduleUpdate = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        update();
      });
    };
    document.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(update);
      this.observer.observe(target);
    }
    update();
  }

  syncFromMain = (event) => {
    if (!this.mainProduct?.contains(event.target)) return;
    if (event.target.matches('[data-variant-choice]')) {
      this.setVariant(event.target.value);
      this.syncQuantityToMain();
    }
    if (event.target.matches('[data-quantity-input], input[name="quantity"]')) {
      this.quantity.value = event.target.value;
      this.normalizeQuantity();
    }
  };

  onChange = (event) => {
    if (event.target === this.select) {
      this.setVariant(this.select.value);
      const choice = this.mainProduct?.querySelector(`[data-variant-choice][value="${CSS.escape(this.select.value)}"]`);
      if (choice && !choice.checked) {
        choice.checked = true;
        choice.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncQuantityToMain();
    }
    if (event.target === this.quantity) this.syncQuantityToMain();
  };

  onClick = (event) => {
    const button = event.target.closest('[data-sticky-quantity]');
    if (button) {
      this.normalizeQuantity();
      this.quantity.value = String(Number(this.quantity.value) + (button.dataset.stickyQuantity === 'plus' ? 1 : -1) * (Number(this.quantity.step) || 1));
      this.syncQuantityToMain();
    }
    if (event.target.closest('[data-sticky-close]')) this.classList.add('is-dismissed');
  };

  setVariant(id) {
    const variant = this.variants.find((variant) => String(variant.id) === String(id));
    if (!variant) return;
    if (this.select) this.select.value = String(id);
    this.idInput.value = String(id);
    if (this.price) this.price.textContent = variant.price;
    if (this.compare) {
      this.compare.textContent = variant.compare;
      this.compare.hidden = !variant.compare;
    }
    if (this.selectedVariant) this.selectedVariant.textContent = variant.title;
    if (this.image && variant.image) {
      this.image.removeAttribute('srcset');
      this.image.src = variant.image;
    }
    this.querySelector('add-to-cart-component').dataset.productVariantMedia = variant.image || '';
    this.addButton.disabled = !variant.available;
    this.label.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    this.quantity.min = String(variant.min);
    this.quantity.step = String(variant.step);
    if (variant.max) this.quantity.max = String(variant.max);
    else this.quantity.removeAttribute('max');
    this.normalizeQuantity();
  }

  normalizeQuantity() {
    const min = Number(this.quantity.min) || 1;
    const step = Number(this.quantity.step) || 1;
    const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
    const value = Math.min(max, Math.max(min, Number(this.quantity.value) || min));
    this.quantity.value = String(min + Math.floor((value - min) / step) * step);
  }

  syncQuantityToMain() {
    this.normalizeQuantity();
    const main = this.mainProduct?.querySelector('[data-quantity-input], input[name="quantity"]');
    if (main) {
      main.value = this.quantity.value;
      main.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('premium-sticky-add-to-cart')) {
  customElements.define('premium-sticky-add-to-cart', PremiumStickyAddToCart);
}
